/** friendlyError (UX spec §3.6, finding A8): map an unknown error to ONE
 *  plain-English banner line, branching ONLY on the typed ApiError's `.status`
 *  via `isApiError` — never on message text. The raw error goes to
 *  console.error for the engineer; the return value is what the user sees.
 *
 *  `action` is a verb phrase ("load outages", "run the bulk action") that
 *  completes "Couldn't <action>." */

import { ApiError, isApiError } from '../api/client.ts'

/** ApiError.detail is typed string, but FastAPI validation errors arrive as a
 *  list of {msg} objects at runtime (client.ts stores body.detail as-is).
 *  Normalize both forms to a display string, or null when unusable. */
function stringDetail(err: ApiError): string | null {
  const raw: unknown = err.detail
  if (typeof raw === 'string' && raw.trim().length > 0) return raw
  if (Array.isArray(raw)) {
    const messages = raw
      .map(item =>
        item && typeof item === 'object' && 'msg' in item
          ? String((item as { msg: unknown }).msg)
          : null,
      )
      .filter((m): m is string => !!m)
    if (messages.length > 0) return messages.join('; ')
  }
  return null
}

export function friendlyError(err: unknown, action: string): string {
  // Raw message/stack for the engineer — never interpolated into the banner.
  console.error(`Failed to ${action}:`, err)
  if (isApiError(err)) {
    const detail = stringDetail(err)
    if (err.status === 403) {
      return detail
        ? `You don't have permission to ${action} — ${detail}`
        : `You don't have permission to ${action}.`
    }
    if (err.status === 404) {
      return `Couldn't ${action} — not found. It may have been removed.`
    }
    if (err.status === 409) {
      return (
        `Couldn't ${action} — changed since you loaded it — refresh and retry.` +
        (detail ? ` (${detail})` : '')
      )
    }
    if (err.status >= 400 && err.status < 500 && detail) {
      // Remaining 4xx (400/422): the server's own actionable message is the
      // friendliest thing available (e.g. "meter 42 no longer exists").
      return detail
    }
  }
  return `Couldn't ${action}. Try again; if it keeps failing, tell the engineer.`
}
